import { useTu } from "@src/appV2/i18n/useTu";
import { isDefined } from "@clipboard-health/util-ts";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { Box, CircularProgress, Link, Popover, Stack } from "@mui/material";
import { useGetAccountManager } from "@src/appV2/ContactCBHInfo/api/useGetAccountManager";
import { useGetBillingSpecialist } from "@src/appV2/ContactCBHInfo/api/useGetFacilityBillingSpecialist";

import {
  BILLING_REP_EMAIL,
  CbhSpecialistType,
  SUPPORT_TELEPHONE,
} from "@src/appV2/ContactCBHInfo/constants";

import { getCBHSpecialistProfile } from "@src/appV2/ContactCBHInfo/utils/getCBHSpecialistProfile";
import { useCurrentUserId } from "@src/appV2/ExperimentalHomeHealth/lib/useCurrentUserId";
import { useIsMobile } from "@src/appV2/redesign/AppLayout/useResponsive";
import { BottomSheet } from "@src/appV2/redesign/components/BottomSheet";
import { Button, type ButtonSize } from "@src/appV2/redesign/components/Button";
import { DialogHeader } from "@src/appV2/redesign/components/DialogHeader";
import { type IconType } from "@src/appV2/redesign/components/Icon.types";
import { Text } from "@src/appV2/redesign/components/Text";

import {
  getBorderBase,
  getMutedText,
  getStrongText,
} from "@src/appV2/redesign/HelpCenter/themeHelpers";

import { useThemeMode } from "@src/appV2/UserPreferences/UserPreferencesProvider";
import { useModalState } from "@src/lib/ui-react";
import { useState } from "react";

interface ContactPopoverButtonProps {
  triggerLabel: string;
  triggerStartIconType?: IconType;
  triggerEndIconType?: IconType;
  triggerSize?: ButtonSize;
  dialogTitle: string;
  showHelpCenterSection: boolean;
  onHelpCenterClick?: () => void;
}

export function ContactPopoverButton(props: ContactPopoverButtonProps) {
  const {
    triggerLabel,
    triggerStartIconType,
    triggerEndIconType,
    triggerSize,
    dialogTitle,
    showHelpCenterSection,
    onHelpCenterClick,
  } = props;

  const isMobile = useIsMobile();
  const { themeMode } = useThemeMode();
  const isDark = themeMode === "dark";
  const navLinkVariant = isDark ? "linkInverted" : "link";
  const workplaceId = useCurrentUserId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const bottomSheetState = useModalState();
  const isOpen = isMobile ? bottomSheetState.modalIsOpen : Boolean(anchorEl);

  // Fetch only while open: page load stays free of specialist calls, and the
  // queries don't keep refetching on window refocus once the popover closes
  // (both hooks are `enabled` only when a facilityId is passed).
  const { data: amData, isLoading: isLoadingAm } = useGetAccountManager({
    facilityId: isOpen ? workplaceId : undefined,
  });

  const { data: billingData, isLoading: isLoadingBilling } = useGetBillingSpecialist({
    facilityId: isOpen ? workplaceId : undefined,
  });

  const amProfile = isDefined(amData)
    ? getCBHSpecialistProfile(amData, CbhSpecialistType.ACCOUNT_MANAGER)
    : undefined;

  const amSpecialist =
    isDefined(amProfile) && amProfile.name
      ? {
          name: amProfile.name,
          phone: amProfile.phone,
          email: amProfile.email,
          workTimings: amProfile.workTimings,
        }
      : undefined;

  const billingProfile = isDefined(billingData)
    ? getCBHSpecialistProfile(billingData, CbhSpecialistType.BILLING_SPECIALIST)
    : undefined;

  const billingSpecialist =
    isDefined(billingProfile) && billingProfile.name
      ? {
          name: billingProfile.name,
          phone: billingProfile.phone ?? SUPPORT_TELEPHONE,
          email: billingProfile.email ?? BILLING_REP_EMAIL,
          workTimings: billingProfile.workTimings,
        }
      : undefined;

  const isLoading = isLoadingAm || isLoadingBilling;

  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    if (isMobile) {
      bottomSheetState.openModal();
    } else {
      setAnchorEl(event.currentTarget);
    }
  }

  // Must stay synchronous end-to-end: the Help Center entry can transitively
  // reach useOpenAsyncUrl, which only works when called inside the original
  // click gesture (Safari popup blocker).
  function handleHelpCenterClick() {
    onHelpCenterClick?.();
    if (isMobile) {
      bottomSheetState.closeModal();
    } else {
      setAnchorEl(null);
    }
  }

  return (
    <>
      <Button
        variant={navLinkVariant}
        size={triggerSize}
        aria-haspopup="true"
        aria-expanded={isOpen}
        startIconType={triggerStartIconType}
        endIconType={triggerEndIconType}
        onClick={handleClick}
      >
        {triggerLabel}
      </Button>
      {isMobile ? (
        <BottomSheet modalState={bottomSheetState}>
          <DialogHeader title={dialogTitle} showCloseButton onClose={bottomSheetState.closeModal} />
          <Box sx={{ overflowY: "auto" }}>
            <ContactSections
              isLoading={isLoading}
              amSpecialist={amSpecialist}
              billingSpecialist={billingSpecialist}
              showHelpCenterSection={showHelpCenterSection}
              onHelpCenterClick={handleHelpCenterClick}
            />
          </Box>
        </BottomSheet>
      ) : (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => {
            setAnchorEl(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{
            "& .MuiPaper-root": {
              borderRadius: 2,
              minWidth: 280,
            },
          }}
        >
          <ContactSections
            isLoading={isLoading}
            amSpecialist={amSpecialist}
            billingSpecialist={billingSpecialist}
            showHelpCenterSection={showHelpCenterSection}
            onHelpCenterClick={handleHelpCenterClick}
          />
        </Popover>
      )}
    </>
  );
}

const cardSx = (theme: Parameters<typeof getBorderBase>[0]) => ({
  borderRadius: theme.borderRadius?.xLarge,
  border: `1px solid ${getBorderBase(theme)}`,
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(4),
  boxShadow: theme.shadow?.card,
});

const sectionTitleSx = (theme: Parameters<typeof getStrongText>[0]) => ({
  color: getStrongText(theme),
  lineHeight: 1.2,
});

interface SpecialistInfo {
  name: string;
  phone?: string;
  email?: string;
  workTimings: string;
}

interface ContactSectionsProps {
  isLoading: boolean;
  amSpecialist: SpecialistInfo | undefined;
  billingSpecialist: SpecialistInfo | undefined;
  showHelpCenterSection: boolean;
  onHelpCenterClick: () => void;
}

function ContactSections(props: ContactSectionsProps) {
  const { isLoading, amSpecialist, billingSpecialist, showHelpCenterSection, onHelpCenterClick } =
    props;
  const { tu } = useTu("navigation");

  return (
    <Stack spacing={2} sx={{ padding: 3 }}>
      <Box sx={cardSx}>
        <Stack spacing={2}>
          <Text bold variant="body1" sx={sectionTitleSx}>
            {tu("24/7 Support")}
          </Text>
          <Stack direction="row" alignItems="center" spacing={2}>
            <PhoneOutlinedIcon fontSize="small" sx={(theme) => ({ color: getMutedText(theme) })} />
            <Link href={`tel:${SUPPORT_TELEPHONE}`} underline="hover" variant="body2">
              {SUPPORT_TELEPHONE}
            </Link>
          </Stack>
        </Stack>
      </Box>
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", paddingY: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {amSpecialist && (
        <SpecialistSection
          title={tu("Account Manager")}
          workTimings={amSpecialist.workTimings}
          name={amSpecialist.name}
          phone={amSpecialist.phone}
          email={amSpecialist.email}
        />
      )}
      {billingSpecialist && (
        <SpecialistSection
          title={tu("Billing Specialist")}
          workTimings={billingSpecialist.workTimings}
          name={billingSpecialist.name}
          phone={billingSpecialist.phone}
          email={billingSpecialist.email}
        />
      )}
      {showHelpCenterSection && (
        <Box sx={cardSx}>
          <Stack spacing={2} alignItems="flex-start">
            <Text bold variant="body1" sx={sectionTitleSx}>
              {tu("Help Center")}
            </Text>
            <Text variant="body2" sx={(theme) => ({ color: getMutedText(theme) })}>
              {tu("Browse help articles and find answers to common questions.")}
            </Text>
            <Button
              variant="outlined"
              size="small"
              endIconType="arrow-right"
              onClick={onHelpCenterClick}
            >
              {tu("Visit Help Center")}
            </Button>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

interface SpecialistSectionProps {
  title: string;
  workTimings: string;
  name: string;
  phone?: string;
  email?: string;
}

function SpecialistSection({ title, workTimings, name, phone, email }: SpecialistSectionProps) {
  return (
    <Box sx={cardSx}>
      <Stack spacing={2}>
        <Box>
          <Text bold variant="body1" sx={sectionTitleSx}>
            {title}
          </Text>
          {workTimings.length > 0 && (
            <Text variant="caption" sx={(theme) => ({ color: getMutedText(theme) })}>
              {workTimings}
            </Text>
          )}
        </Box>
        <Text variant="body2" sx={(theme) => ({ color: getStrongText(theme) })}>
          {name}
        </Text>
        <Stack spacing={1}>
          {phone && (
            <Stack direction="row" alignItems="center" spacing={2}>
              <PhoneOutlinedIcon
                fontSize="small"
                sx={(theme) => ({ color: getMutedText(theme) })}
              />
              <Link href={`tel:${phone}`} underline="hover" variant="body2">
                {phone}
              </Link>
            </Stack>
          )}
          {email && (
            <Stack direction="row" alignItems="center" spacing={2}>
              <EmailOutlinedIcon
                fontSize="small"
                sx={(theme) => ({ color: getMutedText(theme) })}
              />
              <Link href={`mailto:${email}`} underline="hover" variant="body2">
                {email}
              </Link>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
